import moment from 'moment-timezone';
import notifyError from '@/lib/notify-error';
import notifySuccess from '@/lib/notify-success';
import notifyInfo from '@/lib/notify-info';
import {
  findItem, createItem, updateItem,
} from '@/services/publicacao';
import { findTiposuporte } from '@/services/tiposuporte';
import { findIdiomas } from '@/services/idiomas';
import { findSegmentos } from '@/services/segmentos';
import { findEstados } from '@/services/estadoconservacao';
import { deleteFile } from '@/services/container';
import { createDocumento, deleteDocumento, updateDocumento, bulkUpdateNivelDocumentos, bulkDeleteDocumentos } from '@/services/documento';
import { findTipospublicacao } from '@/services/tipospublicacao';
import { findTiposObjeto } from '@/services/tipoObjeto';
import { deletePC } from '@/services/palavraschave';
import { findFamiliaPublicacoes } from '@/services/familiaPublicacao';
import { createNotificacao } from '@/services/notificacao';
import Router from '@/router';

moment.tz.setDefault('UTC');

moment.locale('pt-br');

const usersGetters = {
  editItem(st) {
    return st.editItem;
  },
  isAdmin(st, gt, rSt) {
    if (rSt.auth.account && rSt.auth.account.role[0].name === 'admin') {
      return true;
    }
    return false;
  },
  isOperador(st, gt, rSt) {
    if (rSt.auth.account && rSt.auth.account.role[0].name === 'operador') {
      return true;
    }
    return false;
  },
  loading(st) {
    return st.loading;
  },
  loadingBulkDocumentos(st) {
    return st.loadingBulkDocumentos;
  },
  acervo(st, gt, rSt) {
    const { acervo } = rSt.route.params;
    if (st.tipoPublicacoes.length) return st.tipoPublicacoes.find(tP => tP.etapas === acervo);
    return '';
  },
  suportes(st) {
    return st.suportes;
  },
  idiomas(st) {
    return st.idiomas;
  },
  segmentos(st) {
    return st.segmentos;
  },
  estados(st) {
    return st.estados;
  },
  palavrasChave(st) {
    return st.palavrasChave;
  },
  documentos(st) {
    return st.documentos;
  },
  tiposObjeto(st) {
    return st.tiposObjeto;
  },
  familiaPublicacoes(st) {
    return st.familiaPublicacoes;
  },
  familias(st) {
    return st.familias;
  },
  linksExternos(st) {
    return st.linksExternos;
  },
  footerProps() {
    return {
      itemsPerPageOptions: [6, 12, 24, 48],
      showFirstLastPage: true,
    };
  },
  options() {
    return {
      itemsPerPage: 6,
    };
  },
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },
  setLoadingBulkDocumentos(state, loading) {
    state.loadingBulkDocumentos = loading;
  },
  setEditItem(state, item) {
    state.editItem = item;
  },
  setSavedPublicacao(state, savedPublicacao) {
    state.savedPublicacao = savedPublicacao;
  },
  patchEditItem(state, patch) {
    state.editItem = {
      ...state.editItem,
      ...patch,
    };
  },
  setSuportes(state, suportes) {
    state.suportes = suportes;
  },
  setIdiomas(state, idiomas) {
    state.idiomas = idiomas;
  },
  setSegmentos(state, segmentos) {
    state.segmentos = segmentos;
  },
  setEstados(state, estados) {
    state.estados = estados;
  },
  setPalavrasChave(state, palavrasChave) {
    state.palavrasChave = palavrasChave;
  },
  setDocumentos(state, documentos) {
    state.documentos = documentos;
  },
  addPalavrasChave(state, palavrachave) {
    state.palavrasChave.push({ palavrachave });
  },
  deletePalavrasChave(state, palavrachave) {
    state.palavrasChave = state.palavrasChave
      .filter(pc => pc.palavrachave !== palavrachave.palavrachave);
  },
  setLinksExternos(state, links) {
    state.linksExternos = links;
  },
  addLinkExterno(state, link) {
    state.linksExternos.push(link);
  },
  deleteLinkExterno(state, link) {
    state.linksExternos = state.linksExternos
      .filter(l => l !== link);
  },
  deleteDocumento(state, documento) {
    state.documentos = state.documentos
      .filter(doc => doc.id !== documento.id);
  },
  bulkUpdateNivelDocumentos(state, nivel) {
    state.documentos = state.documentos.map(doc => ({
      ...doc,
      nivel,
    }));
  },
  bulkDeleteDocumentos(state, ids) {
    state.documentos = state.documentos
      .filter(doc => !ids.includes(doc.id));
  },
  setTipoPublicacoes(state, tipoPublicacoes) {
    state.tipoPublicacoes = tipoPublicacoes;
  },
  setTiposObjeto(state, tiposObjeto) {
    state.tiposObjeto = tiposObjeto;
  },
  setFamiliaPublicacoes(state, familiaPublicacoes) {
    state.familiaPublicacoes = familiaPublicacoes;
  },
  setFamilias(state, familias) {
    state.familias = familias;
  },
  addSb(state) {
    state.editItem = {
      ...state.editItem,
      ...state.editItem.suporteBase.push({}),
    };
  },
  removeSb(state, p) {
    const sbs = Object.assign([], state.editItem.suporteBase);
    sbs.splice(p, 1);
    state.editItem = {
      ...state.editItem,
      suporteBase: [
        ...sbs,
      ],
    };
  },
  patchSb(state, { patch, p }) {
    state.editItem.suporteBase[p] = {
      ...state.editItem.suporteBase[p],
      ...patch,
    };
    state.editItem = {
      ...state.editItem,
    };
  },
};

const actions = {
  afterRouteUpdate({ dispatch, commit }, to) {
    commit('setEditItem', { suporteBase: [{}] });
    if (to.params.id) {
      dispatch('loadItem', to.params.id);
    }
    dispatch('findIdiomas');
    dispatch('findTiposuporte');
    dispatch('findSegmentos');
    dispatch('findEstados');
    dispatch('findTipospublicacao');
    dispatch('findTiposObjeto');
    dispatch('findFamiliaPublicacoes');
  },
  loadItem({ commit }, id) {
    commit('setLoading', true);
    const settings = {
      include: ['suporteBase', 'tipoPublicacao', 'documentos', 'segmento', 'idioma', 'palavrasChave', 'tipoObjeto', 'familias'],
    };
    findItem(id, settings)
      .then((item) => {
        item.dtImagem = moment(item.dtImagem).tz('UTC').format('DD/MM/YYYY');
        item.dtLimite = moment(item.dtLimite).tz('UTC').format('DD/MM/YYYY');
        // fix for old structure
        if (!item.fonte && item.autor) {
          item.fonte = item.autor;
        }
        commit('setEditItem', item);
        commit('setPalavrasChave', item.palavrasChave);
        commit('setDocumentos', item.documentos);
        commit('setFamilias', item.familias.map(fm => fm.idFamilia));
        if (item.linkExterno) {
          commit('setLinksExternos', item.linkExterno.split(';'));
        }
        if (item.suporteBase && !item.suporteBase.length) {
          commit('patchSb', { suporteBase: [{}] });
        }
      })
      .catch(notifyError())
      .finally(() => {
        commit('setLoading', false);
      });
  },
  findTipospublicacao({ commit }) {
    findTipospublicacao({ where: { base: 'S' } })
      .then(items => commit('setTipoPublicacoes', items));
  },
  findTiposObjeto({ commit, rootState }) {
    let idTipoPublicacao = 57;
    if (rootState.route.params && rootState.route.params.acervo === 'textual') {
      idTipoPublicacao = 40;
    }
    findTiposObjeto({ where: { idTipopublicacao: idTipoPublicacao }, order: 'tipoObjeto ASC' })
      .then(items => commit('setTiposObjeto', items));
  },
  findIdiomas({ commit }) {
    findIdiomas()
      .then(items => commit('setIdiomas', items));
  },
  findTiposuporte({ commit }) {
    findTiposuporte({ order: 'descricao ASC' })
      .then(items => commit('setSuportes', items));
  },
  findSegmentos({ commit }) {
    findSegmentos({ where: { idCanalPadrao: 273 }, order: 'segmento asc' })
      .then((items) => {
        const copy = JSON.parse(JSON.stringify(items));
        const tmp = copy.splice(7, 2);
        copy.splice(8, 0, tmp[0]);
        copy.splice(8, 0, tmp[1]);
        commit('setSegmentos', copy);
      });
  },
  findEstados({ commit }) {
    findEstados()
      .then(items => commit('setEstados', items));
  },
  findFamiliaPublicacoes({ commit }) {
    findFamiliaPublicacoes()
      .then(items => commit('setFamiliaPublicacoes', items));
  },
  onEditChange({ commit }, event) {
    commit('patchEditItem', event);
  },
  onEditFamiliasChange({ commit }, event) {
    commit('setFamilias', event);
  },
  onAddPalavrasChave({ commit }, palavrachave) {
    if (palavrachave) {
      commit('addPalavrasChave', palavrachave);
    }
  },
  onDeletePalavrasChave({ commit }, palavrachave) {
    if (palavrachave) {
      commit('deletePalavrasChave', palavrachave);
      if (palavrachave.id) {
        deletePC(palavrachave.id);
      }
    }
  },
  onAddLinkExterno({ commit }, link) {
    if (link) {
      commit('addLinkExterno', link);
    }
  },
  onDeleteLinkExterno({ commit }, link) {
    if (link) {
      commit('deleteLinkExterno', link);
    }
  },
  onEditSave({
    commit, state, getters, dispatch, rootState,
  }, $refs) {
    // número de objetos para upload usado na validação
    const files = $refs.myVueDropzone.getQueuedFiles();
    // validação dos campos do formulário + vue-editor
    if (!$refs.editForm.validate() || !state.editItem.conteudo) {
      notifyError('Campos incorretos');
      return false;
    }
    const item = JSON.parse(JSON.stringify(state.editItem));
    // adiciona as escolhas muitos para muitos.
    if (state.linksExternos.length) {
      item.linkExterno = state.linksExternos.join(';');
    } else {
      item.linkExterno = null;
    }
    // adiciona as famílias escolhidas
    if (state.familias.length) {
      item.familias = state.familias;
    }

    // atualiza as palavrasChave
    item.palavrasChave = state.palavrasChave;

    // atribui quem está submetendo
    item.idSubmissao = rootState.auth.account.id;

    // inicia o loading
    commit('setLoading', true);
    // define aviso usada caso tenha uploads
    const info = 'Iniciando os uploads dos anexos. Não feche o seu navegador até a conclusão. Você será redirecionado automaticamente após a conclusão. Arquivos muito grandes podem precisar de mais tempo para processamento.';

    // corrige as datas
    item.dtImagem = moment(item.dtImagem, 'DD/MM/YYYY').tz('UTC').format('YYYY-MM-DD');
    item.dtLimite = moment(item.dtLimite, 'DD/MM/YYYY').tz('UTC').format('YYYY-MM-DD');

    // fix for old structure
    item.autor = '';

    // possui ID é PUT
    if (item.id) {
      // define mensagem de sucesso
      let success = 'Item editado com sucesso!';

      // caso o status era recusado e não é o Admin, manda para avaliação
      if (item.statusFluxo === 'EX' && !getters.isAdmin) {
        item.statusFluxo = 'IN';
        success = 'Acervo enviado para análise com sucesso!';
      }
      // caso é o Admin salvando, publica
      if (getters.isAdmin) {
        item.statusFluxo = 'EV';
      }

      return updateItem(item)
        .then(notifySuccess(success))
        .then((it) => {
          // salva o ID que será usado ao salvar os Documentos
          commit('setSavedPublicacao', it);
          if (files.length) {
            // inicia o upload caso existam anexos
            $refs.myVueDropzone.processQueue();
            notifyInfo(info);
          } else {
            dispatch('finished', $refs.editForm);
          }
          return it;
        })
        .catch((error) => {
          notifyError(error.message);
          commit('setLoading', false);
        });
    }

    // não possui ID é POST
    // define o tipo de acervo
    item.idTipoPublicacao = getters.acervo.idTipopublicacao;

    // define mensagem de sucesso
    let success = 'Item adicionado com sucesso!';

    // define o status de acordo com a permissão
    // Admin: publicado
    // Operador: em avaliação
    if (getters.isAdmin) {
      item.statusFluxo = 'EV';
    } else {
      success = 'Acervo enviado para análise com sucesso!';
      item.statusFluxo = 'IN';
    }

    return createItem(item)
      .then(notifySuccess(success))
      .then((it) => {
        commit('setSavedPublicacao', it);
        if (files.length) {
          // inicia o upload caso existam anexos
          $refs.myVueDropzone.processQueue();
          notifyInfo(info);
        } else {
          dispatch('finished', $refs.editForm);
        }
        return it;
      })
      .catch((error) => {
        notifyError(error.message);
        commit('setLoading', false);
      });
  },
  onFileUploaded({ state, commit }, file) {
    // a cada upload com sucesso, cria um Documento com o ID da Publicação
    let thumbnail = null;
    const splitName = file.renameFilename.split('.');
    const extension = splitName.pop();
    const name = splitName.join('.');
    if (['jpg', 'jpeg', 'png', 'webp', 'tiff', 'raw', 'pdf'].includes(extension.toLowerCase())) {
      thumbnail = `${name}-300.webp`;
    }
    createDocumento({
      idPublicacao: state.savedPublicacao.id,
      tituloDocumento: file.renameFilename,
      nomeInterno: file.renameFilename,
      thumbnail,
      extensaoDoc: file.type,
    })
      .catch((error) => {
        notifyError(error.message);
        commit('setLoading', false);
      });
    return file;
  },
  onFileError(_, { xhr }) {
    if (xhr.response) {
      const { error } = JSON.parse(xhr.response);
      notifyError(`O upload do anexo produziu o seguinte erro: ${error.message}`);
    }
  },
  onFilesAdded({ getters }) {
    if (getters.isAdmin && getters.editItem && getters.editItem.id) {
      notifyInfo(' Para iniciar o carregamento dos anexos é necessário clicar no botão "Salvar".');
    } else if (getters.isAdmin) {
      notifyInfo(' Para iniciar o carregamento dos anexos é necessário clicar no botão "Adicionar".');
    } else {
      notifyInfo('Para iniciar o carregamento dos anexos é necessário clicar no botão "Enviar para análise".');
    }
  },
  onDeleteDocumento({ commit }, documento) {
    deleteDocumento(documento.id)
      .then(notifySuccess('Documento deletado'))
      .then(() => {
        commit('deleteDocumento', documento);
        deleteFile(documento.nomeInterno);
        if (documento.thumbnail) {
          deleteFile(documento.thumbnail);
        }
      })
      .catch((err) => {
        notifyError(err);
      });
  },
  onEditDocumento(_, documento) {
    updateDocumento(documento)
      .then(notifySuccess('Documento editado'))
      .catch((err) => {
        notifyError(err);
      });
  },
  createNotificacao({ state }, tipo) {
    const notificacao = {
      idOperador: state.savedPublicacao.idSubmissao,
      explicacao: 'Publicação para avaliar',
      idPublicacao: state.savedPublicacao.id,
      tipo,
    };
    createNotificacao(notificacao);
  },
  finished({ commit, dispatch, state }, form) {
    // se o status é Em avaliação, cria uma notificação para o Admin revisar
    if (state.savedPublicacao && state.savedPublicacao.statusFluxo === 'IN') {
      dispatch('createNotificacao', 'avaliar');
    }
    dispatch('clean', form);
    Router.push({ name: 'Acervo listar' });
    commit('setLoading', false);
  },
  clean({ commit }, form) {
    if (form) {
      form.reset();
    }
    commit('setEditItem', { suporteBase: [] });
    commit('setPalavrasChave', []);
    commit('setDocumentos', []);
    commit('setFamiliaPublicacoes', []);
    commit('setFamilias', []);
    commit('setLinksExternos', []);
  },
  onAddSb({ commit }) {
    commit('addSb');
  },
  onRemoveSb({ commit }, l) {
    commit('removeSb', l);
  },
  onPatchSb({ commit }, patch) {
    const { p } = patch;
    delete patch.p;
    commit('patchSb', { patch, p });
  },
  onBulkUpdateNivelDocumentos({ commit, state }, nivel) {
    if (!state.documentos || state.documentos.length === 0) {
      notifyInfo('Nenhum documento para atualizar');
      return;
    }
    commit('setLoadingBulkDocumentos', true);
    const ids = state.documentos.map(doc => doc.id);
    bulkUpdateNivelDocumentos(ids, nivel)
      .then(() => {
        commit('bulkUpdateNivelDocumentos', nivel);
      })
      .then(notifySuccess(`Documentos marcados como ${nivel}`))
      .catch((err) => {
        notifyError(err.message || 'Erro ao atualizar documentos');
      })
      .finally(() => {
        commit('setLoadingBulkDocumentos', false);
      });
  },
  onBulkDeleteDocumentos({ commit, state }) {
    if (!state.documentos || state.documentos.length === 0) {
      notifyInfo('Nenhum documento para deletar');
      return;
    }
    commit('setLoadingBulkDocumentos', true);
    const ids = state.documentos.map(doc => doc.id);
    const documentosToDelete = [...state.documentos];
    bulkDeleteDocumentos(ids)
      .then(() => {
        commit('bulkDeleteDocumentos', ids);
        // Delete physical files
        documentosToDelete.forEach((doc) => {
          deleteFile(doc.nomeInterno);
          if (doc.thumbnail) {
            deleteFile(doc.thumbnail);
          }
        });
      })
      .then(notifySuccess('Documentos deletados'))
      .catch((err) => {
        notifyError(err.message || 'Erro ao deletar documentos');
      })
      .finally(() => {
        commit('setLoadingBulkDocumentos', false);
      });
  },
};

export default {
  namespaced: true,
  state: {
    loading: false,
    loadingBulkDocumentos: false,
    editItem: {
      suporteBase: [{}],
    },
    savedPublicacao: {},
    originalRole: null,
    suportes: [],
    idiomas: [],
    segmentos: [],
    estados: [],
    palavrasChave: [],
    documentos: [],
    tipoPublicacoes: [],
    tiposObjeto: [],
    familiaPublicacoes: [],
    familias: [],
    linksExternos: [],
  },
  mutations,
  getters: usersGetters,
  actions,
};
